"use client";

import Link from "next/link";
import { useState } from "react";
import { PlayerCell } from "@/components/casino/LiveStats";
import { Icon } from "@/components/ui/Icon";
import { Panel } from "@/components/ui/primitives";
import { RowsSkeleton, SectionEmpty, SectionError } from "@/components/ui/states";
import { useRaceQualifiedBets } from "@/lib/api/leaderboards";
import { cn } from "@/lib/cn";
import {
  boardError,
  formatMultiplierValue,
  money,
  qualifiedBetEvent,
  qualifiedBetTime,
  type QualifiedBetsPage,
  type RaceProduct,
  type RaceToken,
} from "@/lib/leaderboards/format";

/* The bets that counted towards a race, newest first, the way the kit's
   qualified-bets table lists them: player, what it was on, the stake, the
   multiplier it paid, and when. Paged by cursor, ten at a time; the first
   page stays live, and a game name opens the game. */

export function QualifiedBets({
  raceId,
  token,
  products,
  initial,
}: {
  raceId: string;
  token: RaceToken | null;
  products: RaceProduct[];
  initial?: QualifiedBetsPage;
}) {
  const [page, setPage] = useState<{ number: number; cursor: string | undefined }>({
    number: 1,
    cursor: undefined,
  });
  const feed = useRaceQualifiedBets(raceId, page.cursor, initial);

  const rows = feed.data?.data ?? [];
  const next = feed.data?.next ?? null;
  const previous = feed.data?.previous ?? null;
  const turning = feed.isFetching && feed.isPlaceholderData;
  const eventLabel = products.length === 1 && products[0] === "casino" ? "GAME" : "EVENT";

  const goNext = () => {
    if (next) {
      setPage((current) => ({ number: current.number + 1, cursor: next }));
    }
  };

  /* Back to page one means back to the live page, so the cursor is dropped
     rather than kept — a cursor for the first page would stop the polling. */
  const goPrevious = () =>
    setPage((current) =>
      current.number <= 2
        ? { number: 1, cursor: undefined }
        : { number: current.number - 1, cursor: previous ?? undefined },
    );

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-h6 font-extrabold lg:text-h5">Qualified bets</h2>
          <p className="text-[12px] leading-[1.6] text-fg-subtle">The most recent bets that counted towards this race.</p>
        </div>
        <div className="flex items-center gap-1">
          <span className="pr-1 text-[11px] font-bold text-fg-subtle">Page {page.number}</span>
          <PagerButton
            label="Previous page"
            icon="chevron-left"
            onClick={goPrevious}
            disabled={page.number === 1 || turning}
          />
          <PagerButton label="Next page" icon="chevron-right" onClick={goNext} disabled={!next || turning} />
        </div>
      </div>

      {feed.isError ? (
        <Panel>
          <SectionError title="Qualified bets didn’t load" body={boardError(feed.error)} />
        </Panel>
      ) : feed.isPending ? (
        <Panel>
          <RowsSkeleton rows={5} />
        </Panel>
      ) : rows.length === 0 ? (
        <Panel>
          <SectionEmpty
            icon="target"
            title="No qualified bets yet"
            body="No bet has met this race's rules yet. Place one that does and it shows up here."
          />
        </Panel>
      ) : (
        <Panel className="overflow-hidden">
          <div
            role="region"
            aria-label="Qualified bets"
            tabIndex={0}
            className={cn("scrollbar-none overflow-x-auto transition-opacity", turning && "opacity-60")}
          >
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr className="h-[42px] border-b border-border bg-elevation-2 text-[12px] font-bold leading-[1.6] text-fg-subtle">
                  <th className="pl-[14px] text-left">PLAYER</th>
                  <th className="text-left">{eventLabel}</th>
                  <th className="text-right">WAGERED</th>
                  <th className="text-right">MULTIPLIER</th>
                  <th className="pr-[14px] text-right">TIME</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((bet) => {
                  const event = qualifiedBetEvent(bet);
                  const win = bet.multiplier !== null && bet.multiplier > 1;

                  return (
                    <tr
                      key={bet.id}
                      className="h-[51px] border-b border-border bg-elevation-2 text-[14px] leading-[1.6] transition-colors last:border-0 hover:bg-elevation-3"
                    >
                      <td className="pl-[14px]">
                        <PlayerCell
                          name={bet.account.isHidden ? "Hidden" : bet.account.nickname}
                          accountId={bet.account.isHidden ? undefined : bet.account.id}
                        />
                      </td>
                      <td className="text-fg-subtle">
                        {event.kind === "game" ? (
                          <Link
                            href={`/game/${event.slug}`}
                            className="cursor-pointer font-bold text-fg transition-colors hover:text-brand-fg-accent"
                          >
                            {event.name}
                          </Link>
                        ) : (
                          event.name
                        )}
                      </td>
                      <td className="text-right font-bold tabular-nums">{money(bet.wagered, token)}</td>
                      <td
                        className={cn(
                          "text-right font-bold tabular-nums",
                          win ? "text-success-fg" : "text-fg-subtle",
                        )}
                      >
                        {bet.multiplier === null ? "—" : formatMultiplierValue(bet.multiplier)}
                      </td>
                      <td className="pr-[14px] text-right text-fg-subtle tabular-nums">
                        <time dateTime={bet.createdAt} suppressHydrationWarning>
                          {qualifiedBetTime(bet.createdAt)}
                        </time>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </section>
  );
}

function PagerButton({
  label,
  icon,
  onClick,
  disabled,
}: {
  label: string;
  icon: "chevron-left" | "chevron-right";
  onClick: () => void;
  disabled: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="grid h-[30px] w-[30px] cursor-pointer place-items-center rounded-md bg-elevation-3 text-fg transition-colors hover:bg-elevation-4 disabled:cursor-not-allowed disabled:opacity-40"
    >
      <Icon name={icon} size={16} />
    </button>
  );
}
